/** Shapes returned by the SMS API. Mirrors what `data/store.py` returns. */

export type Severity = "high" | "medium" | "low" | "none";

export interface Risk {
  severity: Severity;
  kind: string;
  detail: string;
}

export interface Student {
  student_id: string;
  preferred_name: string;
  family_name: string;
  program_code: string;
  program_title?: string;
  major?: string;
  commenced?: string;
  citizenship?: string;
  load_status?: string;
  credit_points_completed?: number;
  wam?: number;
  fee_balance_aud?: number;
  holds?: string[];
  risk_count?: number;
  highest_severity?: Severity;
}

export interface Enrolment {
  unit_code: string;
  term: string;
  status: string;
  mark: number | null;
  grade: string | null;
}

export interface Note {
  note_id: string;
  subject: string;
  body: string;
  visibility: "student" | "staff";
  authored_by: string;
  created: string;
}

export interface Fee {
  term: string;
  description: string;
  amount_aud: number;
  due_date: string;
  status: string;
}

export interface Requirements {
  program?: string;
  credit_points_completed?: number;
  credit_points_required?: number;
  credit_points_short?: number;
  credit_points_in_progress?: number;
  outstanding_core_units?: string[];
  core_units_blocked_by_prerequisites?: Record<string, string[]>;
  repeated_failures?: Record<string, number>;
  enrolment_holds?: string[];
}

export interface StudentDetail {
  student: Student | null;
  enrolments: Enrolment[];
  advising_notes: Note[];
  fees: Fee[];
  progression: { requirements: Requirements; risks: Risk[] };
}

export interface ProgramSummary {
  program_code: string;
  title: string;
}

export interface ProgramsResponse {
  current_term: string;
  programs: ProgramSummary[];
}

export interface WhoAmI {
  email: string | null;
  is_staff: boolean;
  student_id: string | null;
}

/** Orderings the list offers. Must match `store.LIST_SORTS`. */
export type SortKey =
  | "name"
  | "student_id"
  | "wam_high"
  | "wam_low"
  | "credits_high"
  | "credits_low";

export const SORT_LABELS: Record<SortKey, string> = {
  name: "Name (A–Z)",
  student_id: "Student ID",
  wam_high: "WAM (high first)",
  wam_low: "WAM (low first)",
  credits_high: "Credit points (most first)",
  credits_low: "Credit points (fewest first)",
};

export interface StudentListResult {
  students: Student[];
  next_cursor: string | null;
  program_code?: string;
  total?: number;
  sort?: SortKey;
}

/** What `POST /admin/reset` undid: notes deleted, students whose holds were restored. */
export interface ResetResult {
  rows_scanned: number;
  notes_removed: number;
  students_holds_restored: number;
  note_keys: string[];
  student_ids: string[];
  dry_run: boolean;
}

export interface StudentSearchResult {
  students: Student[];
  query: string;
  /** True number of matches, even when `students` was capped. */
  total: number;
  truncated: boolean;
}
